import React, { useEffect, useState, Suspense, lazy } from "react";
import { object, func, array } from "prop-types";
import { LazyLoadImages, SocialShare } from "@quintype/components";
import { SocialShareTemplate } from "../../../arrow/components/Molecules/SocialShareTemplate";
import { StoryHeader, LiveBlogHighlights } from "../../molecules";
import AdComponent from "../../molecules/AdComponent";
import { PublishDateAndTime } from "../../atoms";
import { StoryElementCard } from "../../../arrow/components/Molecules/StoryElementCard";
import { LiveIcon } from "../../../arrow/components/Svgs/liveicon";
import { StoryTags } from "../../../arrow/components/Atoms/StoryTags";
import KeyEventCards from "../../../arrow/components/Molecules/KeyEvents";
import { EntityListingStory } from "../../atoms/EntityListingStory";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { useSelector } from "react-redux";
import get from "lodash/get";

import "./live-blog.m.css";

const MetypeCommentsWidgetWrapper = lazy(() => import("../story-elements/metype-comments"));

const filterNonPromotionalCards = (story) => {
  if (!story.cards) return story;

  const filteredCards = story.cards.map((card) => {
    const storyElements = card["story-elements"] || [];
    const cleanElements = storyElements.filter((el) => !get(el, ["metadata", "promotional-message"], false));
    return { ...card, "story-elements": cleanElements };
  });

  return { ...story, cards: filteredCards };
};

const LiveBlogStory = ({ story, config, adWidget, adPlaceholder, hasAccess, recommendedStories, params = {} }) => {
  // Create immutable initial story
  const initialStory = (() => {
    let s = filterNonPromotionalCards(story);
    if (s["story-template"] === "live-blog" && get(s, ["metadata", "is-closed"], false)) {
      s = { ...s, cards: [...s.cards].reverse() };
    }
    return s;
  })();

  const [newStory, setStory] = useState(initialStory);
  const [showHighlights, setShowHighlights] = useState(false);
  const [showCards, setShowCards] = useState(20);
  const [initialCount, setInitialCount] = useState(0);
  const { headline, authors } = newStory;
  const cardsLength = newStory?.cards?.length || 0;
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const qtState = useSelector((state) => state);
  const isAdfreePage = get(qtState, ["qt", "config", "subscriber"]) === "true";
  const isWebView = get(qtState, ["qt", "config", "isWebView"]) === true;
  const metypeTemplates = get(config, ["publisher-attributes", "metypeConfig", "storyTemplate"], []);
  const shouldLoadMetypeComments =
    get(config, ["publisher-attributes", "enableMetype"], false) &&
    Array.isArray(metypeTemplates) &&
    metypeTemplates.includes(story["story-template"]);

  useEffect(() => {
    const intervalId = setInterval(getUpdatedStory, 15000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (showHighlights && isMobile) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }, [showHighlights, isMobile]);

  const scrollToCard = (cardId) => {
    if (cardId) {
      setTimeout(() => {
        const element = document?.getElementById(cardId);
        if (element) {
          const offset = 72;
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: elementPosition - offset,
            behavior: "smooth",
          });
        }
      }, 10);
    }
  };

  useEffect(() => {
    scrollToCard(params.cardId);
  }, [params.cardId]);

  const getUpdatedStory = async () => {
    try {
      const res = await fetch(`/api/v1/stories/${story.id}`);
      const stories = await res.json();
      let updatedStory = stories.story;
      updatedStory = filterNonPromotionalCards(updatedStory);

      if (updatedStory["story-template"] === "live-blog" && get(updatedStory, ["metadata", "is-closed"], false)) {
        updatedStory = {
          ...updatedStory,
          cards: [...updatedStory.cards].reverse(),
        };
      }

      setStory(updatedStory); // triggers re-render with new reference
    } catch (err) {
      console.error("Error fetching updated story:", err);
    }
  };

  const handleReadMore = () => {
    setShowCards((prev) => prev + 10);
    setInitialCount((prev) => prev + 10);
  };

  return (
    <div>
      <LiveBlogHighlights
        story={newStory} // always pass updated state
        isOpen={showHighlights}
        setShowHighlights={setShowHighlights}
        scrollToCard={scrollToCard}
      />
      <div id="live-blog-container" styleName="story-template-container" className="story-holder">
        <StoryHeader
          story={newStory}
          headline={headline}
          storyAuthorData={authors}
          publishTime={newStory?.["published-at"]}
          readTime={newStory?.["read-time"]}
          setShowHighlights={setShowHighlights}
        />

        <EntityListingStory story={newStory} config={config} />

        <div id="story-wrapper" styleName="story-body">
          <KeyEventCards
            story={newStory}
            scrollToCard={scrollToCard}
            config={{
              buttonText: "See More",
            }}
          />
          <div styleName="live-updates">Live Updates</div>

          <div styleName="live-blog-cards">
            {newStory?.cards?.slice(initialCount, showCards).map((card, index) => (
              <React.Fragment key={card.id}>
                <div styleName={`live-blog-card ${index === 0 || index % 2 !== 0 ? "border-extra" : ""}`} id={card.id}>
                  <div styleName="timestamp-and-share">
                    <div styleName="timestamp">
                      <LiveIcon color="#C6554F" />
                      <PublishDateAndTime publishTime={card["card-added-at"]} />
                    </div>
                    {!isWebView && (
                      <div styleName="share-svg">
                        <SocialShare
                          template={SocialShareTemplate}
                          fullUrl={encodeURI(`${newStory.url}#${card.id}`)}
                          title={newStory.headline}
                          shareIconId={`share-icon-story-share-${newStory.id}`}
                          story={newStory}
                        />
                      </div>
                    )}
                  </div>

                  <div styleName="event-content" id={`card${card.id}`}>
                    <LazyLoadImages>
                      <StoryElementCard card={card} story={newStory} key={card.id} />
                    </LazyLoadImages>
                  </div>
                </div>

                {index % 2 === 0 && (
                  <AdComponent
                    recommendedStories={recommendedStories}
                    isDesktop={!isMobile}
                    story={newStory}
                    adIndexNo={index / 2}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {cardsLength >= showCards && (
            <button onClick={handleReadMore} styleName="load-more-button">
              Load More
            </button>
          )}

          <StoryTags tags={newStory.tags} template="noBorder" story={newStory} />
          {shouldLoadMetypeComments && (
            <Suspense fallback={<div></div>}>
              <MetypeCommentsWidgetWrapper story={story} />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
};

LiveBlogStory.propTypes = {
  story: object,
  config: object,
  adWidget: func,
  adPlaceholder: object,
  params: object,
  relatedStories: object,
  hasAccess: func,
  recommendedStories: array,
};

export default LiveBlogStory;
